import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Button,
  Typography,
  Input,
  Textarea,
  Card,
  CardContent,
  Grid,
  Divider,
  Stack,
  FormLabel,
  FormControl,
  Select,
  Option,
  Switch,
} from '@mui/joy';
import { useParams, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { AuthContext } from '../context/AuthContext.jsx';
import PageLayout from '../components/PageLayout';

const PermitDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const [permit, setPermit] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [auditLog, setAuditLog] = useState([]);

  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchPermit = async () => {
      const { data, error } = await supabase
        .from('project_records')
        .select('*')
        .eq('id', id)
        .single();

      if (!error) {
        const updatedData = {
          ...data,
          start_date: data.start_date || new Date().toISOString(),
          application_date: data.application_date || '',
          issue_date: data.issue_date || '',
          expiration_date: data.expiration_date || '',
        };
        setPermit(updatedData);
        setFormData(updatedData);
        // Removed audit log insert for new permit here to avoid duplication.
      }
    };

    const fetchComments = async () => {
      const { data, error } = await supabase
        .from('permit_comments')
        .select('*, users(display_name)')
        .eq('permit_id', id)
        .order('created_at', { ascending: false });

      if (!error) setComments(data);
    };

    const fetchFiles = async () => {
      const { data, error } = await supabase
        .from('permit_documents')
        .select('*')
        .eq('permit_id', id)
        .order('uploaded_at', { ascending: false });
      if (!error) setFiles(data);
    };

    const fetchAuditLog = async () => {
      const { data, error } = await supabase
        .from('permit_audit_log')
        .select('*, users(display_name)')
        .eq('permit_id', id)
        .order('created_at', { ascending: false });
      if (!error) setAuditLog(data);
    };

    fetchPermit();
    fetchComments();
    fetchFiles();
    fetchAuditLog();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    const cleanedData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => {
        if (
          ['start_date', 'application_date', 'issue_date', 'expiration_date'].includes(key) &&
          value === ''
        ) {
          return [key, null];
        }
        return [key, value];
      }).filter(([_, v]) => v !== undefined)
    );
    console.log('📝 Cleaned Form Data:', cleanedData);
    const { error } = await supabase
      .from('project_records')
      .update(cleanedData)
      .eq('id', id);

    if (error) {
      console.error('❌ Error updating project_records:', error.message);
    } else {
      setPermit(formData);
      // Insert audit log for creation or update
      if (location.search.includes('new=true') && cleanedData.name) {
        await supabase.from('permit_audit_log').insert({
          permit_id: id,
          user_id: user?.id,
          action_type: 'created',
          action_detail: `Permit '${cleanedData.name}' was created`
        });
      } else {
        await supabase.from('permit_audit_log').insert({
          permit_id: id,
          user_id: user?.id,
          action_type: 'updated',
          action_detail: JSON.stringify(cleanedData)
        });
      }
      setIsEditing(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !user?.id) return;

    const { error } = await supabase.from('permit_comments').insert([
      {
        permit_id: id,
        user_id: user.id,
        comment: newComment,
        created_by: user.user_metadata?.display_name || user.email,
      },
    ]);

    if (!error) {
      await supabase.from('permit_audit_log').insert({
        permit_id: id,
        user_id: user?.id,
        action_type: 'comment_added',
        action_detail: newComment
      });
      setNewComment('');
      const { data: updatedComments } = await supabase
        .from('permit_comments')
        .select('*, users(display_name)')
        .eq('permit_id', id)
        .order('created_at', { ascending: false });
      setComments(updatedComments);
    }
  };

  if (!permit) return <Typography>Loading...</Typography>;

  return (
    <PageLayout title="Permit Details" fullwidth>
      <Box sx={{ mt: 2, mb: 2 }}>
        <a href="/permits" style={{ color: '#00A884', textDecoration: 'underline' }}>&larr; Back to All Permits</a>
      </Box>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container spacing={2}>
          {/* Actions */}
          <Grid xs={12} sx={{ textAlign: 'left', mt: 2 }} item="true">
            <Button
              onClick={isEditing ? handleSave : () => setIsEditing(true)}
              variant="solid"
              color="primary"
              size="md"
            >
              {isEditing ? 'Save' : 'Edit'}
            </Button>
          </Grid>
          {/* General Info */}
          <Grid xs={12} item="true">
            <Card>
              <CardContent>
                <Typography level="h4" sx={{ color: '#00A884' }}>General Information</Typography>
                <Grid container spacing={2} mt={1}>
                  {['name', 'record_type', 'agency', 'status'].map((field) => (
                    <Grid item xs={12} sm={4} key={field}>
                      <FormLabel>{field.replace(/_/g, ' ')}</FormLabel>
                      {isEditing ? (
                        ['record_type', 'agency', 'status'].includes(field) ? (
                          <Select
                            name={field}
                            value={formData[field] || ''}
                            onChange={(event, newValue) => setFormData(prev => ({ ...prev, [field]: newValue }))}
                            variant="outlined"
                            size="md"
                          >
                            {(field === 'record_type'
                              ? ['Permit', 'Bond', 'Plans']
                              : field === 'agency'
                              ? ['SWFWMD', 'FDEP', 'USACE', 'FDOT']
                              : ['Required', 'Not Required']
                            ).map(option => (
                              <Option key={option} value={option}>{option}</Option>
                            ))}
                          </Select>
                        ) : (
                          <Input name={field} value={formData[field] || ''} onChange={handleChange} variant="outlined" size="md" />
                        )
                      ) : (
                        <Typography>{permit[field] || '—'}</Typography>
                      )}
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={4}>
                    <FormLabel>Archived</FormLabel>
                    {isEditing ? (
                      <Switch
                        checked={formData.archived || false}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, archived: e.target.checked }))
                        }
                      />
                    ) : (
                      <Typography>{permit.archived ? 'Yes' : 'No'}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormLabel>Certified</FormLabel>
                    {isEditing ? (
                      <Switch
                        checked={formData.certified || false}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, certified: e.target.checked }))
                        }
                      />
                    ) : (
                      <Typography>{permit.certified ? 'Yes' : 'No'}</Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Date Info */}
          <Grid xs={12} item="true">
            <Card>
              <CardContent>
                <Typography level="h4" sx={{ color: '#00A884' }}>Dates</Typography>
                <Grid container spacing={2} alignItems="flex-start" mt={1}>
                  <Grid xs={12} sm={3} key="start_date">
                    {isEditing ? (
                      <FormControl>
                        <FormLabel>Start Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.start_date ? formData.start_date.split('T')[0] : ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              start_date: e.target.value || null,
                            }))
                          }
                        />
                      </FormControl>
                    ) : (
                      <>
                        <FormLabel>Start Date</FormLabel>
                        <Typography>
                          {permit.start_date
                            ? new Date(permit.start_date).toLocaleDateString()
                            : '—'}
                        </Typography>
                      </>
                    )}
                  </Grid>
                  <Grid xs={12} sm={3} key="application_date">
                    {isEditing ? (
                      <FormControl>
                        <FormLabel>Application Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.application_date ? formData.application_date.split('T')[0] : ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              application_date: e.target.value || null,
                            }))
                          }
                        />
                      </FormControl>
                    ) : (
                      <>
                        <FormLabel>Application Date</FormLabel>
                        <Typography>
                          {permit.application_date
                            ? new Date(permit.application_date).toLocaleDateString()
                            : '—'}
                        </Typography>
                      </>
                    )}
                  </Grid>
                  <Grid xs={12} sm={3} key="issue_date">
                    {isEditing ? (
                      <FormControl>
                        <FormLabel>Issue Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.issue_date ? formData.issue_date.split('T')[0] : ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              issue_date: e.target.value || null,
                            }))
                          }
                        />
                      </FormControl>
                    ) : (
                      <>
                        <FormLabel>Issue Date</FormLabel>
                        <Typography>
                          {permit.issue_date
                            ? new Date(permit.issue_date).toLocaleDateString()
                            : '—'}
                        </Typography>
                      </>
                    )}
                  </Grid>
                  <Grid xs={12} sm={3} key="expiration_date">
                    {isEditing ? (
                      <FormControl>
                        <FormLabel>Expiration Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.expiration_date ? formData.expiration_date.split('T')[0] : ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              expiration_date: e.target.value || null,
                            }))
                          }
                        />
                      </FormControl>
                    ) : (
                      <>
                        <FormLabel>Expiration Date</FormLabel>
                        <Typography>
                          {permit.expiration_date
                            ? new Date(permit.expiration_date).toLocaleDateString()
                            : '—'}
                        </Typography>
                      </>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Notes */}
          <Grid xs={12} item="true">
            <Card>
              <CardContent>
                <Typography level="h4" sx={{ color: '#00A884' }}>Notes</Typography>
                <Box mt={1}>
                  {isEditing ? (
                    <Textarea name="notes" value={formData.notes || ''} onChange={handleChange} minRows={3} />
                  ) : (
                    <Typography>{permit.notes || '—'}</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Bond Info */}
          {permit.record_type === 'Bond' && (
            <Grid xs={12} item="true">
              <Card>
                <CardContent>
                  <Typography level="h4" sx={{ color: '#00A884' }}>Bond Details</Typography>
                  <Grid container spacing={2} mt={1}>
                    {['bond_amount', 'bond_number', 'surety_company', 'guarantee_type'].map((field) => (
                      <Grid xs={12} sm={6} key={field}>
                        <FormLabel>{field.replace(/_/g, ' ')}</FormLabel>
                        {isEditing ? (
                          <Input name={field} value={formData[field] || ''} onChange={handleChange} variant="outlined" size="md" />
                        ) : (
                          <Typography>{permit[field] || '—'}</Typography>
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Comments */}
          <Grid xs={12} item="true">
            <Card>
              <CardContent>
                <Typography level="h4" sx={{ color: '#00A884' }}>Comments</Typography>
                <Box mt={2}>
                  {comments.map((c) => (
                    <Box key={c.id} sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: 'sm' }}>
                      <Typography level="body-sm" fontWeight="bold">
                        {c.users?.display_name || 'Unknown User'}
                      </Typography>
                      <Typography level="body-sm">
                        {new Date(c.created_at).toLocaleString()}
                      </Typography>
                      <Typography>{c.comment}</Typography>
                    </Box>
                  ))}
                  <Textarea
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    minRows={2}
                    sx={{ mt: 1 }}
                  />
                  <Button onClick={handleAddComment} sx={{ mt: 1 }}>Post Comment</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* File Upload */}
          <Grid xs={12} item="true">
            <Card>
              <CardContent>
                <Typography level="h4" sx={{ color: '#00A884' }}>Permit Files</Typography>
                <Box mt={2}>
                  <Input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                  />
                  <Button
                    sx={{ mt: 1 }}
                    onClick={async () => {
                      if (!selectedFile || !id) return;

                      const filePath = `${id}/${selectedFile.name}`;
                      const { error: uploadError } = await supabase.storage
                        .from('permit-files')
                        .upload(filePath, selectedFile);

                      if (!uploadError) {
                        await supabase.from('permit_documents').insert([
                          {
                            permit_id: id,
                            filename: selectedFile.name,
                            filepath: filePath,
                            uploaded_by: user.id,
                          },
                        ]);
                        await supabase.from('permit_audit_log').insert({
                          permit_id: id,
                          user_id: user?.id,
                          action_type: 'file_uploaded',
                          action_detail: selectedFile.name
                        });
                        alert('File uploaded successfully!');
                        const fetchFiles = async () => {
                          const { data, error } = await supabase
                            .from('permit_documents')
                            .select('*')
                            .eq('permit_id', id)
                            .order('uploaded_at', { ascending: false });
                          if (!error) setFiles(data);
                        };
                        fetchFiles();
                      } else {
                        console.error(uploadError.message);
                        alert('Upload failed.');
                      }
                    }}
                  >
                    Upload File
                  </Button>
                </Box>
                {files.map((file) => {
                  const publicURL = supabase.storage
                    .from('permit-files')
                    .getPublicUrl(file.filepath).data.publicUrl;
                  return (
                    <Box key={file.id} sx={{ mt: 1 }}>
                      <a href={publicURL} target="_blank" rel="noopener noreferrer">
                        {file.filename}
                      </a>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>

          <Grid xs={12} item="true">
            <Card>
              <CardContent>
                <Typography level="h4" sx={{ color: '#00A884' }}>Audit Log</Typography>
                <Box mt={2}>
                  {auditLog.length === 0 && (
                    <Typography>No audit entries yet.</Typography>
                  )}
                  {auditLog.map((log) => (
                    <Box key={log.id} sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: 'sm' }}>
                      <Typography level="body-sm" fontWeight="bold">
                        {log.users?.display_name || 'Unknown User'}
                      </Typography>
                      <Typography level="body-sm">
                        {new Date(log.created_at).toLocaleString()} — {log.action_type}
                      </Typography>
                      <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                        {log.action_detail}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </LocalizationProvider>
    </PageLayout>
  );
};

export default PermitDetail;